import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import InvestorStats from "@/components/admin/InvestorStats";
import InvestorRow from "@/components/admin/InvestorRow";
import { COMMS, INTERESTS, RANGES, STATUSES } from "@/lib/circle";

const COLS = [["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["country", "Country"], ["interest", "Interest"],
    ["amount_range", "Indicative range"], ["communication", "Communication"], ["notes", "Their note"], ["status", "Status"], ["follow_up_status", "Follow-up"],
    ["last_contacted", "Last contacted"], ["internal_notes", "Internal notes"], ["source_label", "Source"], ["referrer", "Referrer"], ["created_at", "Started"],
    ["registered_at", "Registered"], ["privacy_at", "Privacy ack at"], ["marketing", "Marketing consent"], ["marketing_at", "Consent at"], ["marketing_version", "Consent version"], ["unsubscribed", "Unsubscribed"]];

function toCsv(rows) {
    const val = (i, k) => ({ communication: COMMS.find(([c]) => c === i.communication)?.[1], privacy_at: i.privacy_ack?.at, marketing: i.marketing_consent?.granted ? "yes" : "no",
        marketing_at: i.marketing_consent?.at, marketing_version: i.marketing_consent?.version, unsubscribed: i.unsubscribed ? "yes" : "no" }[k] ?? i[k] ?? "");
    const esc = (v) => `"${String(v).replace(/^[=+\-@\t\r]/, "'$&").replace(/"/g, '""')}"`;
    return [COLS.map(([, l]) => esc(l)).join(","), ...rows.map((i) => COLS.map(([k]) => esc(val(i, k))).join(","))].join("\n");
}

export default function AdminInvestors({ client }) {
    const [items, setItems] = useState(null);
    const [stats, setStats] = useState(null);
    const [f, setF] = useState({ q: "", country: "", amount_range: "", interest: "", status: "" });
    const [picked, setPicked] = useState(new Set());

    const load = useCallback(() => {
        client.get("/admin/investors", { params: f }).then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load registrations"));
        client.get("/admin/investors/stats").then(({ data }) => setStats(data)).catch(() => {});
    }, [client, f]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const exportCsv = () => {
        const rows = picked.size ? items.filter((i) => picked.has(i.id)) : items;
        const url = URL.createObjectURL(new Blob(["\ufeff" + toCsv(rows)], { type: "text/csv;charset=utf-8" }));
        Object.assign(document.createElement("a"), { href: url, download: `investor-circle-${new Date().toISOString().slice(0, 10)}.csv` }).click();
        URL.revokeObjectURL(url);
    };
    const togglePick = (id, on) => setPicked((p) => { const n = new Set(p); on ? n.add(id) : n.delete(id); return n; });
    const sel = "rounded-full border border-hairline bg-cream px-4 py-2 text-sm";
    const filter = (k, label, opts) => (
        <select value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} className={sel} data-testid={`ic-filter-${k}`} aria-label={label}>
            <option value="">{label}: all</option>{opts.map((o) => <option key={o}>{o}</option>)}
        </select>
    );

    return (
        <div data-testid="admin-investors">
            <InvestorStats stats={stats} />
            <div className="flex flex-wrap items-center gap-2 mb-6">
                <label className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted_text" />
                    <input value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} placeholder="Search name or email" className={`${sel} pl-9 w-56`} data-testid="ic-search" /></label>
                {filter("country", "Country", Object.keys(stats?.by_country || {}).filter((c) => c !== "—"))}
                {filter("amount_range", "Range", RANGES)}
                {filter("interest", "Interest", INTERESTS)}
                {filter("status", "Status", STATUSES)}
                <button type="button" onClick={load} className="inline-flex items-center gap-2 text-sm text-muted_text hover:text-forest px-2" data-testid="ic-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
                <button type="button" onClick={exportCsv} disabled={!items?.length} className="ml-auto inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2 text-sm disabled:opacity-50" data-testid="ic-export">
                    <Download className="h-4 w-4" /> Export {picked.size ? `${picked.size} selected` : "all shown"} (CSV)
                </button>
            </div>
            {!items ? <Loader2 className="h-6 w-6 animate-spin text-forest" /> : items.length === 0 ? <p className="text-muted_text" data-testid="ic-empty">No registrations match.</p> : (
                <>
                    <label className="flex items-center gap-3 text-sm text-muted_text mb-3"><input type="checkbox" className="h-4 w-4 accent-forest" checked={picked.size === items.length} onChange={(e) => setPicked(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} data-testid="ic-select-all" /> Select all ({items.length})</label>
                    <ul className="space-y-3">
                        {items.map((i) => (
                            <InvestorRow key={i.id} i={i} client={client} selected={picked.has(i.id)} onSelect={(on) => togglePick(i.id, on)}
                                         onChange={(d) => setItems((p) => p.map((x) => (x.id === d.id ? d : x)))}
                                         onDelete={(id) => { setItems((p) => p.filter((x) => x.id !== id)); togglePick(id, false); load(); }} />
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
